// src/components/SearchModal.js - Refactored for workspace architecture
import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useWorkspaces } from './contexts/WorkspacesContext';
import styles from './SearchModal.module.css';

const SearchModal = ({ isOpen, onClose, onDocumentSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const { 
    workspaces, 
    documents,
    selectWorkspace,  // ✅ ADD THIS
    selectDocument    // ✅ ADD THIS
  } = useWorkspaces();
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear results when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Handle search when term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Search through documents
    const results = documents.filter(document => {
      const titleMatch = document.title && document.title.toLowerCase().includes(searchTerm.toLowerCase());
      const contentMatch = document.content && document.content.toLowerCase().includes(searchTerm.toLowerCase());
      return titleMatch || contentMatch;
    });

    setSearchResults(results);
    setIsSearching(false);
  }, [searchTerm, documents]);

  // ✅ FIXED: Switch to document's workspace first, then select the document
  const handleDocumentClick = async (document) => {
    try {
      // First, switch to the document's parent workspace
      if (document.workspace_public_id) {
        await selectWorkspace(document.workspace_public_id);
      }
      
      // Then select the specific document
      selectDocument(document.public_id);
      
      // Call the callback if provided (for backward compatibility)
      if (onDocumentSelect) {
        onDocumentSelect(document.public_id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error selecting document from search:', error);
    }
  };

  // Handle key presses
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div 
        className={styles.modalContent} 
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.modalHeader}>
          <div className={styles.searchInputContainer}>
            <Search className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Search in documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={styles.resultsContainer}>
          {isSearching ? (
            <div className={styles.searching}>Searching...</div>
          ) : searchTerm && searchResults.length === 0 ? (
            <div className={styles.noResults}>No results found</div>
          ) : (
            <div className={styles.resultsList}>
              {searchResults.map(document => {
                // ✅ Find the workspace name for display
                const workspace = workspaces.find(w => w.public_id === document.workspace_public_id);
                
                return (
                  <div 
                    key={document.public_id}
                    className={styles.resultItem}
                    onClick={() => handleDocumentClick(document)}  // ✅ Pass full document object
                  >
                    <h3 className={styles.resultTitle}>
                      {document.title || 'Untitled Document'}
                    </h3>
                    {/* ✅ Show which workspace this document is in */}
                    {workspace && (
                      <div className={styles.workspaceLabel}>
                        📁 {workspace.name || 'Untitled Workspace'}
                      </div>
                    )}
                    {document.content && (
                      <p className={styles.resultPreview}>
                        {stripHtmlTags(document.content).substring(0, 100)}
                        {stripHtmlTags(document.content).length > 100 ? '...' : ''}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to strip HTML tags for preview
function stripHtmlTags(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

export default SearchModal;